/** "New engagement" dialog (WS-5.2). Mentee is a contact record — no account needed (D1). */
import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';

import { useTranslation } from '@/hooks/useTranslation';
import type { EngagementCreateRequest, EngagementLevel, EngagementLanguage } from '@/types/mentorEngagements';
import { ActionButton, SegmentedField, TextField, mentorStyles } from './shared';
import { WorkspaceModal } from './WorkspaceModal';

interface NewEngagementModalProps {
  visible: boolean;
  onClose: () => void;
  onCreate: (payload: EngagementCreateRequest) => Promise<void>;
}

const LEVELS: EngagementLevel[] = ['acc', 'pcc', 'mcc'];

export function NewEngagementModal({ visible, onClose, onCreate }: NewEngagementModalProps) {
  const { t, language } = useTranslation();
  const [menteeName, setMenteeName] = useState('');
  const [menteeEmail, setMenteeEmail] = useState('');
  const [targetLevel, setTargetLevel] = useState<EngagementLevel>('pcc');
  const [formLanguage, setFormLanguage] = useState<EngagementLanguage>(language === 'tr' ? 'tr' : 'en');
  const [startedAt, setStartedAt] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setMenteeName('');
    setMenteeEmail('');
    setTargetLevel('pcc');
    setFormLanguage(language === 'tr' ? 'tr' : 'en');
    setStartedAt(new Date().toISOString().slice(0, 10));
    setError(null);
  }, [visible, language]);

  const submit = async () => {
    if (saving) return;
    if (!menteeName.trim()) {
      setError(t('mentorEngagements.create.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        mentee_name: menteeName.trim(),
        mentee_email: menteeEmail.trim() || null,
        target_level: targetLevel,
        language: formLanguage,
        started_at: startedAt || null,
      });
      onClose();
    } catch {
      setError(t('mentorEngagements.create.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceModal
      testID="new_engagement_modal"
      visible={visible}
      busy={saving}
      title={t('mentorEngagements.create.title')}
      onClose={onClose}
      footer={(
        <>
          <ActionButton label={t('mentorEngagements.create.cancel')} onPress={onClose} disabled={saving} />
          <ActionButton
            testID="new_engagement_submit"
            label={t('mentorEngagements.create.submit')}
            onPress={submit}
            variant="primary"
            disabled={saving}
          />
        </>
      )}
    >
      <TextField
        editable={!saving}
        testID="new_engagement_name"
        label={t('mentorEngagements.create.menteeName')}
        value={menteeName}
        onChangeText={setMenteeName}
        placeholder={t('mentorEngagements.create.menteeNamePlaceholder')}
      />
      <TextField
        editable={!saving}
        label={t('mentorEngagements.create.menteeEmail')}
        value={menteeEmail}
        onChangeText={setMenteeEmail}
        placeholder={t('mentorEngagements.create.menteeEmailPlaceholder')}
        keyboardType="email-address"
      />
      <View style={mentorStyles.row}>
        <SegmentedField<EngagementLevel>
          label={t('mentorEngagements.create.targetLevel')}
          options={LEVELS.map((level) => ({ value: level, label: level.toUpperCase() }))}
          value={targetLevel}
          disabled={saving}
          onChange={setTargetLevel}
        />
        <SegmentedField<EngagementLanguage>
          label={t('mentorEngagements.create.language')}
          options={[
            { value: 'en' as EngagementLanguage, label: 'EN' },
            { value: 'tr' as EngagementLanguage, label: 'TR' },
          ]}
          value={formLanguage}
          disabled={saving}
          onChange={setFormLanguage}
        />
      </View>
      <TextField
        editable={!saving}
        label={t('mentorEngagements.create.startedAt')}
        value={startedAt}
        onChangeText={setStartedAt}
        placeholder="YYYY-MM-DD"
      />
      {error ? <Text style={mentorStyles.errorText}>{error}</Text> : null}
    </WorkspaceModal>
  );
}

export default NewEngagementModal;
